/* eslint-disable max-lines, no-prototype-builtins */

import type {Feature, FeatureCollection, Geometry} from 'geojson';
import isEqual from 'lodash/isEqual';
import type {
    AnyLayer,
    AnyLayout,
    AnyPaint,
    AnySourceData,
    GeoJSONSource,
    Layer,
    LngLatLike,
    Map,
    Sources,
    Style,
    Transition,
} from 'mapbox-gl';

type MapInstance = InstanceType<typeof Map>;

export type GeoJSONData = Feature<Geometry> | FeatureCollection<Geometry> | string;

export const operations = {
    // { command: 'setStyle', args: [stylesheet] }
    setStyle: 'setStyle',

    // { command: 'addLayer', args: [layer, 'beforeLayerId'] }
    addLayer: 'addLayer',

    // { command: 'removeLayer', args: ['layerId'] }
    removeLayer: 'removeLayer',

    // { command: 'setPaintProperty', args: ['layerId', 'prop', value] }
    setPaintProperty: 'setPaintProperty',

    // { command: 'setLayoutProperty', args: ['layerId', 'prop', value] }
    setLayoutProperty: 'setLayoutProperty',

    // { command: 'setFilter', args: ['layerId', filter] }
    setFilter: 'setFilter',

    // { command: 'addSource', args: ['sourceId', source] }
    addSource: 'addSource',

    // { command: 'removeSource', args: ['sourceId'] }
    removeSource: 'removeSource',

    // { command: 'setGeoJSONSourceData', args: ['sourceId', data] }
    setGeoJSONSourceData: 'setGeoJSONSourceData',

    // { command: 'setLayerZoomRange', args: ['layerId', 0, 22] }
    setLayerZoomRange: 'setLayerZoomRange',

    // { command: 'setLayerProperty', args: ['layerId', 'prop', value] }
    setLayerProperty: 'setLayerProperty',

    // { command: 'setCenter', args: [[lon, lat]] }
    setCenter: 'setCenter',

    // { command: 'setZoom', args: [zoom] }
    setZoom: 'setZoom',

    // { command: 'setBearing', args: [bearing] }
    setBearing: 'setBearing',

    // { command: 'setPitch', args: [pitch] }
    setPitch: 'setPitch',

    // { command: 'setSprite', args: ['spriteUrl'] }
    setSprite: 'setSprite',

    // { command: 'setGlyphs', args: ['glyphsUrl'] }
    setGlyphs: 'setGlyphs',

    // { command: 'setTransition', args: [transition] }
    setTransition: 'setTransition',

    // { command: 'setLighting', args: [lightProperties] }
    setLight: 'setLight',

    setTerrain: 'setTerrain',

    setFog: 'setFog',
} as const;

type Operation = keyof typeof operations;

interface DiffOperationBase {
    command: Operation;
    args: unknown[];
}

interface AddLayerOperation extends DiffOperationBase {
    command: 'addLayer';
    args: Parameters<MapInstance['addLayer']>;
}

interface SetStyleOperation extends DiffOperationBase {
    command: 'setStyle';
    args: Parameters<MapInstance['setStyle']>;
}

interface RemoveLayerOperation extends DiffOperationBase {
    command: 'removeLayer';
    args: Parameters<MapInstance['removeLayer']>;
}

interface SetPaintPropertyOperation extends DiffOperationBase {
    command: 'setPaintProperty';
    args: Parameters<MapInstance['setPaintProperty']>;
}

interface SetLayoutPropertyOperation extends DiffOperationBase {
    command: 'setLayoutProperty';
    args: Parameters<MapInstance['setLayoutProperty']>;
}

interface SetFilterOperation extends DiffOperationBase {
    command: 'setFilter';
    args: Parameters<MapInstance['setFilter']>;
}

interface AddSourceOperation extends DiffOperationBase {
    command: 'addSource';
    args: Parameters<MapInstance['addSource']>;
}

interface RemoveSourceOperation extends DiffOperationBase {
    command: 'removeSource';
    args: Parameters<MapInstance['removeSource']>;
}

interface SetGeoJSONSourceDataOperation extends DiffOperationBase {
    command: 'setGeoJSONSourceData';
    args: [string, GeoJSONData];
}

interface SetLayerZoomRangeOperation extends DiffOperationBase {
    command: 'setLayerZoomRange';
    args: Parameters<MapInstance['setLayerZoomRange']>;
}

interface SetLayerPropertyOperation extends DiffOperationBase {
    command: 'setLayerProperty';
    args: [string, string, unknown];
}

interface SetCenterOperation extends DiffOperationBase {
    command: 'setCenter';
    args: Parameters<MapInstance['setCenter']>;
}

interface SetZoomOperation extends DiffOperationBase {
    command: 'setZoom';
    args: Parameters<MapInstance['setZoom']>;
}

interface SetBearingOperation extends DiffOperationBase {
    command: 'setBearing';
    args: Parameters<MapInstance['setBearing']>;
}

interface SetPitchOperation extends DiffOperationBase {
    command: 'setPitch';
    args: Parameters<MapInstance['setPitch']>;
}

interface SetSpriteOperation extends DiffOperationBase {
    command: 'setSprite';
    args: [string | undefined];
}

interface SetGlyphsOperation extends DiffOperationBase {
    command: 'setGlyphs';
    args: [string];
}

interface SetTransitionOperation extends DiffOperationBase {
    command: 'setTransition';
    args: [Transition];
}

interface SetLightOperation extends DiffOperationBase {
    command: 'setLight';
    args: Parameters<MapInstance['setLight']>;
}

interface SetTerrainOperation extends DiffOperationBase {
    command: 'setTerrain';
    args: Parameters<MapInstance['setTerrain']>;
}

interface SetFogOperation extends DiffOperationBase {
    command: 'setFog';
    args: Parameters<MapInstance['setFog']>;
}

export type DiffOperation =
    | AddLayerOperation
    | SetStyleOperation
    | RemoveLayerOperation
    | SetPaintPropertyOperation
    | SetLayoutPropertyOperation
    | SetFilterOperation
    | AddSourceOperation
    | RemoveSourceOperation
    | SetGeoJSONSourceDataOperation
    | SetLayerZoomRangeOperation
    | SetLayerPropertyOperation
    | SetCenterOperation
    | SetZoomOperation
    | SetBearingOperation
    | SetPitchOperation
    | SetSpriteOperation
    | SetGlyphsOperation
    | SetTransitionOperation
    | SetLightOperation
    | SetTerrainOperation
    | SetFogOperation;

/**
 * Diff two stylesheet
 *
 * Creates semanticly aware diffs that can easily be applied at runtime.
 * Operations produced by the diff closely resemble the mapbox-gl-js API. Any
 * error creating the diff will fall back to the 'setStyle' operation.
 *
 * Example diff:
 * [
 *     { command: 'setConstant', args: ['@water', '#0000FF'] },
 *     { command: 'setPaintProperty', args: ['background', 'background-color', 'black'] }
 * ]
 *
 */
export function diffStyles(before: Style, after: Style): DiffOperation[] {
    if (!before) return [{command: operations.setStyle, args: [after]}];

    let commands: DiffOperation[] = [];

    try {
        // Handle changes to top-level properties
        if (!isEqual(before.version, after.version)) {
            return [{command: operations.setStyle, args: [after]}];
        }
        if (!isEqual(before.center, after.center)) {
            commands.push({
                command: operations.setCenter,
                args: [after.center as LngLatLike],
            });
        }
        if (!isEqual(before.zoom, after.zoom)) {
            commands.push({command: operations.setZoom, args: [after.zoom!]});
        }
        if (!isEqual(before.bearing, after.bearing)) {
            commands.push({
                command: operations.setBearing,
                args: [after.bearing!],
            });
        }
        if (!isEqual(before.pitch, after.pitch)) {
            commands.push({command: operations.setPitch, args: [after.pitch!]});
        }
        if (!isEqual(before.sprite, after.sprite)) {
            commands.push({
                command: operations.setSprite,
                args: [after.sprite],
            });
        }
        if (!isEqual(before.glyphs, after.glyphs)) {
            commands.push({
                command: operations.setGlyphs,
                args: [after.glyphs!],
            });
        }
        if (!isEqual(before.transition, after.transition)) {
            commands.push({
                command: operations.setTransition,
                args: [after.transition!],
            });
        }
        if (!isEqual(before.light, after.light)) {
            commands.push({command: operations.setLight, args: [after.light!]});
        }
        if (!isEqual(before.fog, after.fog)) {
            commands.push({command: operations.setFog, args: [after.fog!]});
        }

        // If the terrain was removed, remove it before removing its source
        if (!isEqual(before.terrain, after.terrain) && !after.terrain) {
            commands.push({command: operations.setTerrain, args: [undefined]});
        }

        // Handle changes to `sources`
        // If a source is to be removed, we also--before the removeSource
        // command--need to remove all the style layers that depend on it.
        const sourcesRemoved = {};

        // First collect the {add,remove}Source commands
        const removeOrAddSourceCommands: DiffOperation[] = [];
        diffSources(before.sources, after.sources, removeOrAddSourceCommands, sourcesRemoved);

        // Push a removeLayer command for each style layer that depends on a
        // source that's being removed.
        // Also, exclude any such layers them from the input to `diffLayers`
        // below, so that diffLayers produces the appropriate `addLayers`
        // command
        const beforeLayers: AnyLayer[] = [];
        before.layers?.forEach((layer) => {
            // @ts-ignore
            if (sourcesRemoved[layer.source]) {
                commands.push({
                    command: operations.removeLayer,
                    args: [layer.id],
                });
            } else {
                beforeLayers.push(layer);
            }
        });

        commands = commands.concat(removeOrAddSourceCommands);

        // If terrain was added, add it after adding its source
        if (!isEqual(before.terrain, after.terrain) && after.terrain) {
            commands.push({
                command: operations.setTerrain,
                args: [after.terrain],
            });
        }

        // Handle changes to `layers`
        diffLayers(beforeLayers, after.layers, commands);
    } catch (e) {
        // fall back to setStyle

        if (DEPLOYMENT !== 'production') console.warn('Unable to compute style diff:', e);
        commands = [{command: operations.setStyle, args: [after]}];
    }

    return commands;
}

function diffSources(
    before: Sources = {},
    after: Sources = {},
    commands: DiffOperation[],
    sourcesRemoved: Record<string, boolean>
): void {
    before = before || {};
    after = after || {};

    let sourceId;

    // look for sources to remove
    for (sourceId in before) {
        if (!before.hasOwnProperty(sourceId)) continue;
        if (!after.hasOwnProperty(sourceId)) {
            removeSource(sourceId, commands, sourcesRemoved);
        }
    }

    // look for sources to add/update
    for (sourceId in after) {
        if (!after.hasOwnProperty(sourceId)) continue;
        if (!before.hasOwnProperty(sourceId)) {
            addSource(sourceId, after, commands);
        } else if (!isEqual(before[sourceId], after[sourceId])) {
            if (
                before[sourceId].type === 'geojson' &&
                after[sourceId].type === 'geojson' &&
                canUpdateGeoJSON(before, after, sourceId)
            ) {
                commands.push({
                    command: operations.setGeoJSONSourceData,
                    args: [sourceId, (after[sourceId] as GeoJSONSource & {data: GeoJSONData}).data],
                });
            } else {
                // no update command, must remove then add
                updateSource(sourceId, after, commands, sourcesRemoved);
            }
        }
    }
}

function removeSource(
    sourceId: string,
    commands: DiffOperation[],
    sourcesRemoved: Record<string, boolean>
): void {
    commands.push({command: operations.removeSource, args: [sourceId]});
    sourcesRemoved[sourceId] = true;
}

function addSource(sourceId: string, after: Sources, commands: DiffOperation[]): void {
    commands.push({
        command: operations.addSource,
        args: [sourceId, after[sourceId]],
    });
}

function updateSource(
    sourceId: string,
    after: Sources,
    commands: DiffOperation[],
    sourcesRemoved: Record<string, boolean>
): void {
    removeSource(sourceId, commands, sourcesRemoved);
    addSource(sourceId, after, commands);
}

function canUpdateGeoJSON(before: Sources, after: Sources, sourceId: string): boolean {
    for (const prop in before[sourceId]) {
        if (!before[sourceId].hasOwnProperty(prop)) continue;
        if (
            prop !== 'data' &&
            !isEqual(
                before[sourceId][prop as keyof AnySourceData],
                after[sourceId][prop as keyof AnySourceData]
            )
        ) {
            return false;
        }
    }

    for (const prop in after[sourceId]) {
        if (!after[sourceId].hasOwnProperty(prop)) continue;
        if (
            prop !== 'data' &&
            !isEqual(
                before[sourceId][prop as keyof AnySourceData],
                after[sourceId][prop as keyof AnySourceData]
            )
        ) {
            return false;
        }
    }
    return true;
}

function diffLayers(before: Layer[] = [], after: Layer[] = [], commands: DiffOperation[]): void {
    // order of layers by id
    const beforeOrder = before.map(pluckId);
    const afterOrder = after.map(pluckId);

    // index of layer by id
    const beforeIndex = before.reduce(indexById, {});
    const afterIndex = after.reduce(indexById, {});

    // track order of layers as if they have been mutated
    const tracker = beforeOrder.slice();

    // layers that have been added do not need to be diffed
    const clean = Object.create(null);

    let i: number,
        d: number,
        layerId: string,
        beforeLayer: Layer,
        afterLayer: Layer,
        insertBeforeLayerId: string,
        prop: keyof Layer;

    // remove layers
    for (i = 0, d = 0; i < beforeOrder.length; i++) {
        layerId = beforeOrder[i];
        if (!afterIndex.hasOwnProperty(layerId)) {
            commands.push({command: operations.removeLayer, args: [layerId]});
            tracker.splice(tracker.indexOf(layerId, d), 1);
        } else {
            // limit where in tracker we need to look for a match
            d++;
        }
    }

    // add/reorder layers
    for (i = 0, d = 0; i < afterOrder.length; i++) {
        // work backwards as insert is before an existing layer
        layerId = afterOrder[afterOrder.length - 1 - i];

        if (tracker[tracker.length - 1 - i] === layerId) continue;

        if (beforeIndex.hasOwnProperty(layerId)) {
            // remove the layer before we insert at the correct position
            commands.push({command: operations.removeLayer, args: [layerId]});
            tracker.splice(tracker.lastIndexOf(layerId, tracker.length - d), 1);
        } else {
            // limit where in tracker we need to look for a match
            d++;
        }

        // add layer at correct position
        insertBeforeLayerId = tracker[tracker.length - i];
        commands.push({
            command: operations.addLayer,
            args: [afterIndex[layerId] as AnyLayer, insertBeforeLayerId],
        });
        tracker.splice(tracker.length - i, 0, layerId);
        clean[layerId] = true;
    }

    // update layers
    for (i = 0; i < afterOrder.length; i++) {
        layerId = afterOrder[i];
        beforeLayer = beforeIndex[layerId];
        afterLayer = afterIndex[layerId];

        // no need to update if previously added (new or moved)
        if (clean[layerId] || isEqual(beforeLayer, afterLayer)) continue;

        // If source, source-layer, or type have changes, then remove the layer
        // and add it back 'from scratch'.
        if (
            !isEqual(beforeLayer.source, afterLayer.source) ||
            !isEqual(beforeLayer['source-layer'], afterLayer['source-layer']) ||
            !isEqual(beforeLayer.type, afterLayer.type)
        ) {
            commands.push({command: operations.removeLayer, args: [layerId]});
            // we add the layer back at the same position it was already in, so
            // there's no need to update the `tracker`
            insertBeforeLayerId = tracker[tracker.lastIndexOf(layerId) + 1];
            commands.push({
                command: operations.addLayer,
                args: [afterLayer as AnyLayer, insertBeforeLayerId],
            });
            continue;
        }

        // layout, paint, filter, minzoom, maxzoom
        diffLayerLayoutPropertyChanges(beforeLayer.layout, afterLayer.layout, commands, layerId);
        diffLayerPaintPropertyChanges(beforeLayer.paint, afterLayer.paint, commands, layerId, null);
        if (!isEqual(beforeLayer.filter, afterLayer.filter)) {
            commands.push({
                command: operations.setFilter,
                args: [layerId, afterLayer.filter],
            });
        }
        if (
            !isEqual(beforeLayer.minzoom, afterLayer.minzoom) ||
            !isEqual(beforeLayer.maxzoom, afterLayer.maxzoom)
        ) {
            commands.push({
                command: operations.setLayerZoomRange,
                args: [layerId, afterLayer.minzoom!, afterLayer.maxzoom!],
            });
        }

        // handle all other layer props, including paint.*
        for (prop in beforeLayer) {
            if (!beforeLayer.hasOwnProperty(prop)) continue;
            if (
                prop === 'layout' ||
                prop === 'paint' ||
                prop === 'filter' ||
                prop === 'metadata' ||
                prop === 'minzoom' ||
                prop === 'maxzoom'
            )
                continue;
            if (prop.startsWith('paint.')) {
                diffLayerPaintPropertyChanges(
                    beforeLayer[prop as keyof Layer],
                    afterLayer[prop as keyof Layer],
                    commands,
                    layerId,
                    prop.slice(6)
                );
            } else if (!isEqual(beforeLayer[prop], afterLayer[prop])) {
                commands.push({
                    command: operations.setLayerProperty,
                    args: [layerId, prop, afterLayer[prop]],
                });
            }
        }
        for (prop in afterLayer) {
            if (!afterLayer.hasOwnProperty(prop) || beforeLayer.hasOwnProperty(prop)) continue;
            if (
                prop === 'layout' ||
                prop === 'paint' ||
                prop === 'filter' ||
                prop === 'metadata' ||
                prop === 'minzoom' ||
                prop === 'maxzoom'
            )
                continue;
            if (prop.startsWith('paint.')) {
                diffLayerPaintPropertyChanges(
                    beforeLayer[prop as keyof Layer],
                    afterLayer[prop as keyof Layer],
                    commands,
                    layerId,
                    prop.slice(6)
                );
            } else if (!isEqual(beforeLayer[prop], afterLayer[prop])) {
                commands.push({
                    command: operations.setLayerProperty,
                    args: [layerId, prop, afterLayer[prop]],
                });
            }
        }
    }
}

function pluckId(layer: Layer): string {
    return layer.id;
}

function indexById(group: Record<string, Layer>, layer: Layer): Record<string, Layer> {
    group[layer.id] = layer;
    return group;
}

function diffLayerLayoutPropertyChanges(
    before: AnyLayout = {},
    after: AnyLayout = {},
    commands: DiffOperation[],
    layerId: string
): void {
    for (const prop in before) {
        const layoutProp = prop as keyof AnyLayout;

        if (!before.hasOwnProperty(layoutProp)) continue;

        if (!isEqual(before[layoutProp as keyof AnyLayout], after[layoutProp as keyof AnyLayout])) {
            commands.push({
                command: 'setLayoutProperty',
                args: [layerId, layoutProp, after[layoutProp as keyof AnyLayout]],
            });
        }
    }

    for (const prop in after) {
        const layoutProp = prop as keyof AnyLayout;

        if (!after.hasOwnProperty(layoutProp) || before.hasOwnProperty(layoutProp)) continue;

        if (!isEqual(before[layoutProp], after[layoutProp])) {
            commands.push({
                command: 'setLayoutProperty',
                args: [layerId, layoutProp, after[layoutProp]],
            });
        }
    }
}

// eslint-disable-next-line max-params
function diffLayerPaintPropertyChanges(
    before: AnyPaint = {},
    after: AnyPaint = {},
    commands: DiffOperation[],
    layerId: string,
    klass: string | null
) {
    for (const prop in before) {
        const paintProp = prop as keyof AnyPaint;

        if (!before.hasOwnProperty(paintProp)) continue;

        if (!isEqual(before[paintProp], after[paintProp])) {
            commands.push({
                command: 'setPaintProperty',
                args: [layerId, paintProp, after[paintProp], klass!],
            });
        }
    }

    for (const prop in after) {
        const paintProp = prop as keyof AnyPaint;

        if (!after.hasOwnProperty(paintProp) || before.hasOwnProperty(paintProp)) continue;

        if (!isEqual(before[paintProp], after[paintProp])) {
            if (klass === null) {
                commands.push({
                    command: 'setPaintProperty',
                    args: [layerId, paintProp, after[paintProp]],
                });
            } else {
                commands.push({
                    command: 'setPaintProperty',
                    args: [layerId, paintProp, after[paintProp], klass],
                });
            }
        }
    }
}
